/* eslint-disable class-methods-use-this */
import { cloneDeep, get, isEqual, unset, set } from "lodash";

import BlockUpdateV0 from "../../vo/BlockUpdate";
import PropertyMaps from "../../constants/PropertyMaps";
import ValidationUtils from "../ValidationUtils";
import AppConstants from "../../constants/AppConstants";
import {
  gatherProps,
  getProp,
  prepForInherit,
  getSubPropsList,
  propIsParent,
  shouldPropMerge,
  expandPropsList,
} from "../Merge/MergeUtils.mjs";
import {
  SHADOW_STYLE,
  TEXT_COLUMNS,
  TEXT_COLUMNS_GAP,
  TEXT_STYLE,
} from "../../constants/MergeConstants";
import { EFFECTS, DEFAULT_MERGE_PROPS } from "../../constants/SyncConstants";
import {
  GALLERY,
  GRAPHIC,
  ICON,
  LINE,
  SIMPLE,
  SOCIAL,
  TEXT,
} from "../../constants/DataTypes";
import { ensureArray } from "../JSUtils";
import { Map } from "immutable";

/**
 * @typedef {import("immutable").Map} ImmutableMap
 */

const {
  SIMPLE_PROPS,
  SIMPLE_SYNC_PROPS,
  ICON_PROPS,
  LINE_PROPS,
  SOCIAL_GRID_SYNC_PROPS,
  GALLERY_PROPS,
  GALLERY_SYNC_PROPS,
  ELEMENT_TRANSITION_PROPS,
  ELEMENT_TRANSITION_SYNC_PROPS,
  GATHERABLE_TEXT_PROPS,
  TEXT_SYNC_PROPS,
} = PropertyMaps;
const {
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  UPDATE_ACTION_SET,
  UPDATE_ACTION_DELETE,
  SYNCED_PROPS,
} = AppConstants;

const COPY_PROP_MAP = {
  [ICON]: [ICON_PROPS],
  [GALLERY]: [GALLERY_PROPS, GALLERY_SYNC_PROPS],
  [LINE]: [LINE_PROPS],
  [SIMPLE]: [SIMPLE_PROPS, SIMPLE_SYNC_PROPS],
  [SOCIAL]: [SOCIAL_GRID_SYNC_PROPS],
};

const getOtherLayout = (layout) =>
  layout === LAYOUT_MOBILE ? LAYOUT_DESKTOP : LAYOUT_MOBILE;

/**
 * @typedef {import("../Merge/MergeUtils.mjs").SyncableEntity} SyncableEntity
 * @typedef {import("../Merge/MergeUtils.mjs").Layout} Layout
 */

class ElementUtils {
  /**
   * Gets the latest update to sync from the updater, if it exists.
   * @param {BlockUpdateV0} updater
   * @returns {string[]=}
   */
  getLatestSyncUpdate = (updater) => {
    if (updater?.updates?.length) {
      const previousSyncUpdate = [...updater.updates]
        .reverse()
        .find(
          (update) =>
            update?.action === "set" &&
            update?.key.slice(-1)[0] === SYNCED_PROPS,
        );

      if (previousSyncUpdate) {
        return previousSyncUpdate?.value;
      }
    }
  };

  /**
   * Creates a new element with the the target layout props applied, the other layout props split out
   * (in case they happen to be merged) and the sync array without the syncable props included.
   * It looks for previous updates in the sync array to prevent wiping from the base element every time,
   * since the entire array is replaced for every "set".
   * @param {SyncableEntity} source
   * @param {Layout} sourceLayout
   * @param {SyncableEntity} targetElement
   * @param {Layout} targetLayout
   * @param {*} theme
   * @param {string[]} gatherableProps
   * @param {string[]=} syncableProps
   * @returns
   */
  cloneForDiff = (
    source,
    sourceLayout,
    targetElement,
    targetLayout,
    providedBlockUpdator,
    theme,
    gatherableProps,
    syncableProps,
  ) => {
    const syncProps = syncableProps || gatherableProps;
    const otherTargetLayout = getOtherLayout(targetLayout);

    return {
      ...cloneDeep(targetElement),
      [targetLayout]: gatherProps(
        source,
        sourceLayout,
        gatherableProps,
        true,
        theme,
      ),
      [otherTargetLayout]: gatherProps(
        targetElement,
        otherTargetLayout,
        gatherableProps,
        true,
        theme,
      ),
      sync: (
        this.getLatestSyncUpdate(providedBlockUpdator) ||
        targetElement?.sync ||
        []
      ).filter((syncProp) => !syncProps.includes(syncProp)),
    };
  };

  /**
   * Coordinates the prop application process between a source and a list of elements. It then attempts to
   * re-sync and then generate a list of formatted updates for the updater to apply.
   * providedSyncProps and providedGatherableProps can be a function and use the current targetElement as context.
   * @param {SyncableEntity} source
   * @param {Layout} sourceLayout
   * @param {SyncableEntity[] | SyncableEntity} targets
   * @param {Layout} targetLayout
   * @param {BlockUpdateV0=} providedBlockUpdator
   * @param {*} theme
   * @param {string[]} gatherableProps
   * @param {(targetElement: SyncableEntity) => string[] | string[]=} providedSyncProps
   * @param {(targetElement: SyncableEntity, newTarget: SyncableEntity) => SyncableEntity=} modifyTargetCallback
   * @returns
   */
  applyProps = (
    source,
    sourceLayout,
    targets,
    targetLayout,
    providedBlockUpdator,
    theme,
    providedGatherableProps,
    providedSyncProps,
    modifyTargetCallback,
  ) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();
    const updates = [];

    ensureArray(targets).forEach((targetElement) => {
      const gatherableProps =
        typeof providedGatherableProps === "function"
          ? providedGatherableProps(targetElement)
          : providedGatherableProps;

      const syncProps =
        typeof providedSyncProps === "function"
          ? providedSyncProps(targetElement)
          : providedSyncProps || gatherableProps;

      let newTarget = this.cloneForDiff(
        source,
        sourceLayout,
        targetElement,
        targetLayout,
        blockUpdator,
        theme,
        gatherableProps,
        syncProps,
      );

      if (modifyTargetCallback) {
        newTarget = modifyTargetCallback(targetElement, newTarget);
      }

      newTarget = this.reSyncProps(newTarget, syncProps);
      updates.push(
        ...this.generateDiffUpdates(newTarget, targetElement, gatherableProps),
      );
    });

    updates.forEach((update) => blockUpdator.addPropUpdate(...update));

    return blockUpdator;
  };

  /**
   * Returns false if an inner shadow is applied to an element other than simple or graphic.
   * @param {SyncableEntity} element
   * @param {Layout} layout
   * @returns
   */
  isShadowValid = (element, layout) => {
    const { type } = element;
    const shadowValue = get(element, `${layout}.${SHADOW_STYLE}`);
    return shadowValue === "inner" ? [SIMPLE, GRAPHIC].includes(type) : true;
  };

  applyHover = (elements, newHover, providedBlockUpdator) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();

    if (!ValidationUtils.validateParams(newHover)) {
      return blockUpdator;
    }

    const elementArray = Array.isArray(elements) ? elements : [elements];
    elementArray.forEach((element) => {
      const updatePayload = {
        name: "hover",
        value: newHover,
      };

      blockUpdator.addPropUpdate(element.selected.id, updatePayload);
    });

    return blockUpdator;
  };

  // removes excess/non-applicable hover properties for element
  filterHover2 = (elements, blockUpdator) => {
    const elementArray = Array.isArray(elements) ? elements : [elements];
    elementArray.forEach((element) => {
      let hoverPropMap;

      switch (element.type) {
        case AppConstants.ELEMENT_TYPE_SIMPLE:
          hoverPropMap = PropertyMaps.HOVER_PROPS.SIMPLE;
          break;
        case AppConstants.ELEMENT_TYPE_TEXT:
          hoverPropMap = PropertyMaps.HOVER_PROPS.TEXT;
          break;
        case AppConstants.ELEMENT_TYPE_ICON:
          hoverPropMap = PropertyMaps.HOVER_PROPS.ICON;
          break;
        default:
          hoverPropMap = PropertyMaps.HOVER_PROPS.COMMON;
          break;
      }

      element?.hover &&
        Object.keys(element.hover).forEach((hoverProp) => {
          if (!hoverPropMap.find((prop) => prop === hoverProp)) {
            blockUpdator.deleteProp(element.selected.id, ["hover", hoverProp]);
          }
        });
    });
  };

  /**
   *
   * @param {SyncableEntity} source
   * @param {*} sourceLayout
   * @param {*} targets
   * @param {*} targetLayout
   * @param {*} providedBlockUpdator
   * @param {Object} theme
   * @returns
   */
  matchTextFormat = (
    source,
    sourceLayout,
    targets,
    targetLayout,
    providedBlockUpdator,
    theme,
  ) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();
    const gatherableProps =
      targetLayout === LAYOUT_DESKTOP && sourceLayout === LAYOUT_DESKTOP
        ? GATHERABLE_TEXT_PROPS
        : GATHERABLE_TEXT_PROPS.filter(
            (propName) => ![TEXT_COLUMNS, TEXT_COLUMNS_GAP].includes(propName),
          );

    const modifyTextCallback = (targetElement, newTarget) => {
      const targetStyleWasSynced = targetElement?.sync?.includes(TEXT_STYLE);
      const stylesMatch =
        getProp(targetElement, getOtherLayout(targetLayout), TEXT_STYLE) ===
        getProp(source, sourceLayout, TEXT_STYLE);

      /**
       * set type to style it is supposed to end up as.
       * This prevents the filter from removing incorrect props (as in what should be + styles for the target).
       * */
      if (targetStyleWasSynced || stylesMatch) {
        const style = targetStyleWasSynced
          ? getProp(targetElement, LAYOUT_DESKTOP, TEXT_STYLE)
          : getProp(newTarget, targetLayout, TEXT_STYLE);
        set(newTarget, `${LAYOUT_DESKTOP}.${TEXT_STYLE}`, style);
        set(newTarget, `${LAYOUT_MOBILE}.${TEXT_STYLE}`, style);
      }

      return this.filterThemeMatches(newTarget, theme);
    };

    this.applyProps(
      source,
      sourceLayout,
      targets,
      targetLayout,
      blockUpdator,
      theme,
      gatherableProps,
      TEXT_SYNC_PROPS,
      modifyTextCallback,
    );

    return blockUpdator;
  };

  /**
   * Attempts to re-sync element based on syncList.
   * @param {SyncableEntity} newElement - Syncable entity
   * @param {String[]} syncList - List of props to sync on
   * @returns
   */
  reSyncProps = (newElement, syncList) => {
    syncList.forEach((propName) => {
      if (shouldPropMerge(newElement, propName)) {
        newElement.sync.push(propName);
        unset(newElement, `${LAYOUT_MOBILE}.${propName}`);

        if (propIsParent(newElement, propName)) {
          // unset sub props
          const value = get(newElement, `${LAYOUT_DESKTOP}.${propName}`);
          getSubPropsList(propName, value).forEach((subProp) =>
            unset(newElement, `${LAYOUT_MOBILE}.${subProp}`),
          );
        }
      }
    });

    return newElement;
  };

  /**
   * Filters out prop values that match the theme for that layout.
   * Uses theme gathered prop in case the value is synced within the theme.
   * @param {SyncableEntity} element - Element to filter props from
   * @param {Theme} theme - Design theme
   * @returns {Object} - Element with filtered props
   */
  filterThemeMatches = (element, theme) => {
    [LAYOUT_DESKTOP, LAYOUT_MOBILE].forEach((layout) => {
      TEXT_SYNC_PROPS.filter((propName) => propName !== TEXT_STYLE).forEach(
        (propName) => {
          const [preparedTheme, themePropName] = prepForInherit(
            element,
            layout,
            propName,
            theme,
          );
          const themeValue = getProp(preparedTheme, layout, themePropName);
          const propValue = get(element, `${layout}.${propName}`);

          if (isEqual(themeValue, propValue)) {
            unset(element, `${layout}.${propName}`);
          }
        },
      );
    });

    return element;
  };

  /**
   * Generates updates based on new element vs the original
   * @param {SyncableEntity} element - New element (or one to compare against)
   * @param {SyncableEntity} target - Original target element
   * @param {String[]} propList - List of props that updates can be generated from. Usually sync props.
   */
  generateDiffUpdates = (newElement, target, propList) => {
    const updates = [];
    const addUpdate = (layout, propName, value, action = UPDATE_ACTION_SET) => {
      const updateName = layout ? `${layout}.${propName}` : propName;
      const updatePayload = {
        name: updateName,
        value,
        action,
      };
      updates.push([newElement.selected.id, updatePayload]);
    };

    // sync is always updated
    addUpdate(undefined, SYNCED_PROPS, newElement.sync);
    // both layouts have to be checked since even just desktop changes can affect mobile
    [LAYOUT_DESKTOP, LAYOUT_MOBILE].forEach((layout) => {
      const originalLayoutProps = get(target, layout);
      const modifiedLayoutProps = get(newElement, layout);
      propList.forEach((propName) => {
        // add update if layoutProp differs from the original
        if (
          this.layoutPropDiffers(
            originalLayoutProps,
            modifiedLayoutProps,
            propName,
          )
        ) {
          const value = get(modifiedLayoutProps, propName);
          [undefined, null].includes(value)
            ? addUpdate(layout, propName, null, UPDATE_ACTION_DELETE)
            : addUpdate(layout, propName, value);
        }
      });
    });

    return updates;
  };

  /**
   * Checks for difference in layout props. A/B layouts provided separately so they can come from any source
   * ex. a modified desktop layout vs the original
   * @param {*} aLayout
   * @param {*} bLayout
   * @param {Layout} propName
   */
  layoutPropDiffers = (aLayout, bLayout, propName) =>
    !isEqual(get(aLayout, propName), get(bLayout, propName));

  resetHover = (elements, providedBlockUpdator) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();

    if (!ValidationUtils.validateParams(elements)) {
      return blockUpdator;
    }

    const elementArray = Array.isArray(elements) ? elements : [elements];
    elementArray.forEach((element) => {
      blockUpdator.deleteProp(element.selected.id, ["hover"], blockUpdator);
    });

    return blockUpdator;
  };

  setEffects = (
    source,
    sourceLayout,
    targets,
    targetLayout,
    providedBlockUpdator,
    theme,
  ) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();
    const getSyncProps = (targetElement) =>
      DEFAULT_MERGE_PROPS[targetElement.type].filter((propName) =>
        EFFECTS.includes(propName),
      );

    const getGatherableProps = (sourceElement) =>
      expandPropsList(source, getSyncProps(sourceElement));

    const filterShadow = (_, newTarget) => {
      if (!this.isShadowValid(newTarget, targetLayout)) {
        expandPropsList(newTarget, [SHADOW_STYLE]).forEach((propName) =>
          unset(newTarget, `${targetLayout}.${propName}`),
        );
      }
      return newTarget;
    };

    this.applyProps(
      source,
      sourceLayout,
      targets,
      targetLayout,
      blockUpdator,
      theme,
      getGatherableProps,
      getSyncProps,
      filterShadow,
    );

    return blockUpdator;
  };

  setHover = (elements, newHover, providedBlockUpdator) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();

    this.resetHover(elements, blockUpdator);
    this.applyHover(elements, newHover, blockUpdator);
    this.filterHover2(elements, blockUpdator);

    return blockUpdator;
  };

  setTransitions = (
    source,
    sourceLayout,
    targets,
    targetLayout,
    providedBlockUpdator,
    theme,
  ) => {
    const blockUpdator = providedBlockUpdator || new BlockUpdateV0();

    this.applyProps(
      source,
      sourceLayout,
      targets,
      targetLayout,
      providedBlockUpdator,
      theme,
      ELEMENT_TRANSITION_PROPS,
      ELEMENT_TRANSITION_SYNC_PROPS,
    );

    return blockUpdator;
  };

  /**
   *  Starts copy process for valid elements. Returns blockUpdator with updates.
   * @param {SyncableEntity} source
   * @param {Layout} sourceLayout
   * @param {SyncableEntity[] | SyncableEntity} targets
   * @param {Layout} targetLayout
   * @param {BlockUpdateV0=} providedBlockUpdator
   * @param {ImmutableMap} theme
   * @returns {BlockUpdateV0}
   */
  handleCopy = (
    source,
    sourceLayout,
    targets,
    targetLayout,
    providedBlockUpdator,
    theme,
  ) => {
    const preparedTheme = Map.isMap(theme) ? theme.toJS() : theme ?? {}

    const props = [
      source,
      sourceLayout,
      targets,
      targetLayout,
      providedBlockUpdator,
      preparedTheme
    ];
    const { type } = source;

    switch (type) {
      case ICON:
      case GALLERY:
      case LINE:
      case SOCIAL:
      case SIMPLE:
        return this.applyProps(...props, ...COPY_PROP_MAP[type]);
      case TEXT:
        return this.matchTextFormat(...props);
      default:
        return providedBlockUpdator;
    }
  };
}

export default new ElementUtils();
